import React, { useEffect, useState, useRef, Suspense } from 'react';
import lazyWithRetry from './utils/lazyWithRetry';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Login from './components/Login';
import AppToaster from './components/AppToaster';
import { initRechargeWithMode } from './utils/rechargeProxyMode';
import './App.css';
import useSessionValidator from './hooks/useSessionValidator';
import SessionExpiredModal from './components/SessionExpiredModal';
import AddPaymentMethodPromptModal from './components/AddPaymentMethodPromptModal';
import PendingChargeModal from './components/PendingChargeModal';
import LoadingOverlay from './components/LoadingOverlay';
import SavingSelectionGuard from './components/SavingSelectionGuard';
import ImpersonationBanner from './components/ImpersonationBanner';
import OutageBanner from './components/OutageBanner';
import { logout } from './utils/auth';
import { refreshRechargeSession, maybeRenewShopifyToken } from './utils/sessionRefresh';
import { loginWithShopifyStorefront, fetchPaymentMethods, fetchSubscriptions } from './services/recharge';
import FloatingHelpButton from './components/FloatingHelpButton';
import RedesignInviteModal from './components/RedesignInviteModal';
import PwaInstallBanner from './components/PwaInstallBanner';
import useBoxSelectionStore from './services/boxSelectionService';
import { ProductRatingProvider } from './context/ProductRatingContext';
import { resetScrollLock } from './utils/scrollLock';
import useIsTestVersion from './hooks/useIsTestVersion';
// Import font files to ensure they're included in the build
import './assets/fonts/Apercu_Regular.otf';
import './assets/fonts/Idler_Pro_Solid.otf';

// Route-level code splitting: each page loads as its own chunk on first visit,
// keeping the initial bundle to the app shell. Login stays eager so the
// unauthenticated entry point renders without a second round-trip.
const Overview = lazyWithRetry(() => import('./pages/Overview'));
const Gifting = lazyWithRetry(() => import('./pages/Gifting'));
const Rewards = lazyWithRetry(() => import('./pages/Rewards'));
const Account = lazyWithRetry(() => import('./pages/Account'));
const History = lazyWithRetry(() => import('./pages/History'));
const Help = lazyWithRetry(() => import('./pages/Help'));
const NewSubscription = lazyWithRetry(() => import('./pages/NewSubscription'));
const GiftRedemption = lazyWithRetry(() => import('./pages/GiftRedemption'));
const AdminImpersonationPage = lazyWithRetry(() => import('./pages/AdminImpersonation'));
const MagicLinkRedirect = lazyWithRetry(() => import('./pages/MagicLinkRedirect'));
const Impact = lazyWithRetry(() => import('./pages/Impact'));
const Feedback = lazyWithRetry(() => import('./pages/Feedback'));
const MyReviews = lazyWithRetry(() => import('./pages/MyReviews'));
const BakingInstructions = lazyWithRetry(() => import('./pages/BakingInstructions'));
const OrderSubstitution = lazyWithRetry(() => import('./pages/OrderSubstitution'));
const ForgotPassword = lazyWithRetry(() => import('./components/ForgotPassword'));

// Protected route component
const ProtectedRoute = ({ children }) => {
  // Check for Shopify auth tokens in URL first
  const urlParams = new URLSearchParams(window.location.search);
  const hasAuthTokens = urlParams.get('storefrontAccessToken') || urlParams.get('customerAccessToken');
  const isAuthenticated = localStorage.getItem('rechargeSession') !== null;

  // If we have auth tokens in URL, don't redirect yet - let the auth handler process them
  if (!isAuthenticated && !hasAuthTokens) {
    // Store the current path as the target URL for post-login redirect
    const currentPath = window.location.pathname + window.location.search;
    localStorage.setItem('loginRedirectTarget', currentPath);
    return <Navigate to="/login" replace />;
  }
  return children;
};

// Redirect component that preserves query parameters
const RedirectWithParams = ({ to }) => {
  const location = useLocation();
  const searchParams = location.search;
  const destination = searchParams ? `${to}${searchParams}` : to;
  return <Navigate to={destination} replace />;
};

// Safety net: if any modal/sheet leaks the body-scroll lock (mismatched
// useEffect cleanups, error during close, etc.), navigating to a new route
// would leave the customer with a frozen page until they hard-reload.
// Forcing the lock back to zero on every route change costs us nothing in
// the happy path (no modals are open during navigation) and rescues the
// page in the unhappy one.
const ScrollLockResetter = () => {
  const location = useLocation();
  useEffect(() => {
    resetScrollLock();
  }, [location.pathname]);
  return null;
};

// Component to handle payment method checking (runs once after auth is ready)
const PaymentMethodChecker = ({ 
  rechargeInitialized, 
  hasValidated, 
  setShowPaymentMethodModal 
}) => {
  const location = useLocation();
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    const checkPaymentMethodsAndSubscriptions = async () => {
      // Paths considered part of the authentication flow - don't check on these
      const authPaths = ['/login', '/forgot-password', '/admin/access', '/magiclink/redirect'];
      const isAuthPage = authPaths.some((p) => location.pathname.startsWith(p));
      
      // Only check if authenticated, not on auth page, and Recharge is initialized
      const hasSession = localStorage.getItem('rechargeSession') !== null;
      if (!hasSession || isAuthPage || !rechargeInitialized || !hasValidated || hasCheckedRef.current) {
        return;
      }

      hasCheckedRef.current = true;
      try {
        const [paymentMethods] = await Promise.all([
          fetchPaymentMethods(),
          fetchSubscriptions()  // warm the cache for other components
        ]);
        
        const hasPaymentMethods = paymentMethods && paymentMethods.length > 0;

        // Show modal if no payment methods
        if (!hasPaymentMethods) {
          setShowPaymentMethodModal(true);
        } else {
          // Hide modal if payment methods exist
          setShowPaymentMethodModal(false);
        }
      } catch (error) {
        console.error('Error checking payment methods and subscriptions:', error);
        // Don't show modal on error - fail silently
      }
    };

    checkPaymentMethodsAndSubscriptions();
  }, [rechargeInitialized, hasValidated, setShowPaymentMethodModal, location.pathname]);

  return null;
};

function App() {
  const [rechargeInitialized, setRechargeInitialized] = useState(false);
  const { isExpired, hasValidated } = useSessionValidator({ interval: 45_000 });
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [expiredStableFor, setExpiredStableFor] = useState(0);
  const [showPaymentMethodModal, setShowPaymentMethodModal] = useState(false);
  const [showPendingChargeModal, setShowPendingChargeModal] = useState(false);
  // Respond to low-level session-expired events (thrown by services/recharge)
  useEffect(() => {
    const handler = () => {
      // Suppress the modal on auth pages; the magic-link flow will write a
      // fresh session in moments and any stale-token race shouldn't surface here.
      const authPaths = ['/login', '/forgot-password', '/admin/access', '/magiclink/redirect'];
      const isAuthPage = authPaths.some((p) => window.location.pathname.startsWith(p));
      if (isAuthPage) return;
      setShowExpiredModal(true);
    };
    window.addEventListener('session-expired', handler);
    return () => window.removeEventListener('session-expired', handler);
  }, []);

  // Respond to pending-charge-error events (thrown by boxSelectionService)
  useEffect(() => {
    const handler = () => {
      setShowPendingChargeModal(true);
    };
    window.addEventListener('pending-charge-error', handler);
    return () => window.removeEventListener('pending-charge-error', handler);
  }, []);
  const showModalTimeoutRef = useRef(null);
  const [isAuthenticatingWithToken, setIsAuthenticatingWithToken] = useState(false);
  
  // Subscribe to box selection saving state
  const isSavingBoxSelection = useBoxSelectionStore((state) => state.isSaving);
  const isTest = useIsTestVersion();

  // Initialize ReCharge once at app startup
  useEffect(() => {
    const initializeRecharge = async () => {
      try {
        initRechargeWithMode({
          // Called by the SDK on any 401. Returning a session makes it retry the
          // failed request transparently, so a lapsed token costs the member
          // nothing; only an impossible refresh falls through to logout.
          loginRetryFn: async () => {
            const session = await refreshRechargeSession();
            if (session) return session;
            logout();
            return null;
          },
        });
        setRechargeInitialized(true);
      } catch (error) {
        console.error('Error initializing ReCharge client:', error);
        if (error.message && error.message.includes('already initialized')) {
          setRechargeInitialized(true);
        }
      }
    };
    initializeRecharge();
  }, []);

  // Top up the Shopify token when it nears expiry — it's what silent refresh
  // trades on, so extending it extends how long members stay logged in.
  useEffect(() => {
    if (!localStorage.getItem('rechargeSession')) return;
    maybeRenewShopifyToken();
  }, []);

  // Handle authentication via query parameters from the app
  useEffect(() => {
    const handleQueryParamAuth = async () => {
      setIsAuthenticatingWithToken(true); // Set loading state

      const urlParams = new URLSearchParams(window.location.search);
      const storefrontAccessToken = urlParams.get('storefrontAccessToken');
      const customerAccessToken = urlParams.get('customerAccessToken');

      if (storefrontAccessToken || customerAccessToken) {
        try {
          // Use the URL's storefront token if provided, otherwise use env token
          const storefrontTokenToUse = storefrontAccessToken || process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN;

          const session = await loginWithShopifyStorefront(
            storefrontTokenToUse,
            customerAccessToken
          );

          if (session && session.apiToken) {
            // A token without a customer can never load member data (the SDK
            // throws "Not logged in." on every customer-scoped read), so never
            // store one, whether or not Recharge attached a message.
            if (!session.customerId) {
              console.error('Authentication issue:', session.message || 'session has no customer id');
              setIsAuthenticatingWithToken(false);
              return;
            }

            // Save the authentication session
            localStorage.setItem('rechargeSession', JSON.stringify(session));

            // Clean up URL by removing auth params
            const newUrl = new URL(window.location.href);
            newUrl.searchParams.delete('storefrontAccessToken');
            newUrl.searchParams.delete('customerAccessToken');
            window.history.replaceState({}, document.title, newUrl.toString());

            // Reload to initialize with new session
            window.location.reload();
          } else {
            console.error('No session returned from authentication');
            setIsAuthenticatingWithToken(false);
          }
        } catch (error) {
          console.error('Failed to authenticate with Shopify tokens:', error);
          setIsAuthenticatingWithToken(false);
        }
      } else {
        setIsAuthenticatingWithToken(false);
      }
    };

    // Check immediately if we have tokens in URL
    const urlParams = new URLSearchParams(window.location.search);
    const hasTokensInUrl = urlParams.get('storefrontAccessToken') || urlParams.get('customerAccessToken');

    // Only run this if Recharge is initialized and we don't already have a session
    const hasExistingSession = !!localStorage.getItem('rechargeSession');

    if (hasTokensInUrl && !hasExistingSession && rechargeInitialized) {
      handleQueryParamAuth();
    }
  }, [rechargeInitialized]);

  // Handle session expiration with improved debouncing
  useEffect(() => {
    // Clear any existing timeout
    if (showModalTimeoutRef.current) {
      clearTimeout(showModalTimeoutRef.current);
      showModalTimeoutRef.current = null;
    }
    
    // Check if we're on an auth page - if so, don't show modal
    const authPaths = ['/login', '/forgot-password', '/admin/access', '/magiclink/redirect'];
    const isAuthPage = authPaths.some((p) => window.location.pathname.startsWith(p));
    
    if (isExpired && !isAuthPage) {
      // Increment stability counter
      setExpiredStableFor(prev => prev + 1);
      
      // Only show modal if expired state has been stable for multiple checks
      if (expiredStableFor >= 2) {
        showModalTimeoutRef.current = setTimeout(() => {
          setShowExpiredModal(true);
        }, 3000); // 3 second delay
      }
    } else {
      // Session is not expired OR we're on auth page - reset everything immediately
      setExpiredStableFor(0);
      setShowExpiredModal(false);
      
      // Clear any pending timeout
      if (showModalTimeoutRef.current) {
        clearTimeout(showModalTimeoutRef.current);
        showModalTimeoutRef.current = null;
      }
    }
    
    return () => {
      if (showModalTimeoutRef.current) {
        clearTimeout(showModalTimeoutRef.current);
        showModalTimeoutRef.current = null;
      }
    };
  }, [isExpired]); // Removed expiredStableFor and showExpiredModal from dependencies to prevent loops

  // Reset modal when validation shows we have a valid session
  useEffect(() => {
    if (!isExpired && hasValidated) {
      // Session is valid - ensure modal is hidden
      setShowExpiredModal(false);
      setExpiredStableFor(0);
      if (showModalTimeoutRef.current) {
        clearTimeout(showModalTimeoutRef.current);
        showModalTimeoutRef.current = null;
      }
    }
  }, [isExpired, hasValidated]);


  // Paths considered part of the authentication flow. While on any of these
  // routes, we never show the "Session expired" modal.
  const authPaths = ['/login', '/forgot-password', '/admin/access', '/magiclink/redirect'];
  // While redeeming a gift, we should not block the flow with the add-payment-method prompt.
  const giftRedeemPaths = ['/gift/redeem'];

  // Detect if the current route is part of the gift-redemption flow.
  const isGiftRedemptionPage = giftRedeemPaths.some((p) => window.location.pathname.startsWith(p));
   
  // Determine whether the current path starts with **any** auth path. Using
  // startsWith() instead of direct equality guards against optional trailing
  // slashes or nested paths (e.g. "/login/reset").
  const isAuthPage = authPaths.some((p) => window.location.pathname.startsWith(p));

  // Block render only while Recharge is initializing (synchronous, one effect
  // tick) or while exchanging URL tokens for a session (page reloads after).
  //
  // Note we deliberately do NOT block on the initial session validation
  // round-trip: pages render their skeletons immediately while
  // useSessionValidator runs in the background. If the token turns out to be
  // expired, the SDK's loginRetryFn (logout -> /login) and the
  // 'session-expired' event -> SessionExpiredModal handle it reactively.
  // Blocking here used to add a full Recharge API round-trip to LCP on every
  // page load.
  if (!rechargeInitialized || isAuthenticatingWithToken) {
    const message = isAuthenticatingWithToken ? "Authenticating with provided tokens..." : "Loading your account...";
    return <LoadingOverlay fullScreen message={message} size="lg" />;
  }

  // Show the session-expired modal whenever our validator has determined
  // the session is no longer valid and we are not on an auth-related page.
  // We no longer require that the stale token still exists in storage because
  // `getRechargeSession()` may have proactively cleared it.
  const showSessionModal = showExpiredModal && !isAuthPage;

  return (
    <Router>
      <ProductRatingProvider>
        <ScrollLockResetter />
        <PaymentMethodChecker
          rechargeInitialized={rechargeInitialized}
          hasValidated={hasValidated}
          setShowPaymentMethodModal={setShowPaymentMethodModal}
        />
        <div className="font-['Apercu']">
        {/* While saving the box: new look keeps the page browsable (no blur)
            and only blocks actions; old look keeps the fullscreen overlay. */}
        {isSavingBoxSelection && (
          isTest ? (
            <SavingSelectionGuard message="Saving your selection..." />
          ) : (
            <LoadingOverlay
              fullScreen
              message="Saving your selection..."
              size="lg"
            />
          )
        )}
        {/* Session expired modal (hidden on login page) */}
        <SessionExpiredModal isOpen={showSessionModal} />
        {/* Pending charge modal */}
        <PendingChargeModal 
          isOpen={showPendingChargeModal} 
          onClose={() => setShowPendingChargeModal(false)} 
        />
        {/* Add payment method prompt modal */}
        <AddPaymentMethodPromptModal
          isOpen={showPaymentMethodModal && !showSessionModal && !isGiftRedemptionPage}
          onClose={() => setShowPaymentMethodModal(false)}
        />
        {/* One-time "try the new design" invite for rollout-invited existing members */}
        <RedesignInviteModal
          blocked={showSessionModal || showPaymentMethodModal || showPendingChargeModal}
        />
        {/* Technical outage banner */}
        <OutageBanner />
        {/* Impersonation mode banner */}
        <ImpersonationBanner />
        <Suspense fallback={<LoadingOverlay fullScreen message="Loading..." size="lg" />}>
        <RouteErrorBoundary>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          {/* Nested so /overview ↔ /overview/<id> share one Overview instance
              (prevents unmount that wiped magic-link modal state). */}
          <Route
            path="/overview"
            element={
              <ProtectedRoute>
                <Overview />
              </ProtectedRoute>
            }
          >
            <Route path="otp" element={null} />
            <Route path=":subscription_id" element={null} />
          </Route>
          <Route
            path="/gifting"
            element={
              <ProtectedRoute>
                <Gifting />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rewards"
            element={
              <ProtectedRoute>
                <Rewards />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <Account />
              </ProtectedRoute>
            }
          />
          <Route 
            path="/history" 
            element={
              <ProtectedRoute>
                <History />
              </ProtectedRoute>
            } 
          />
          <Route path="/help" element={<Help />} />
          <Route 
            path="/new" 
            element={
              <ProtectedRoute>
                <NewSubscription />
              </ProtectedRoute>
            } 
          />
          <Route path="/admin/access" element={<AdminImpersonationPage />} />
          <Route path="/magiclink/redirect" element={<MagicLinkRedirect />} />
          <Route 
            path="/gift/redeem" 
            element={
              <ProtectedRoute>
                <GiftRedemption />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/impact" 
            element={
              <ProtectedRoute>
                <Impact />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/feedback" 
            element={
              <ProtectedRoute>
                <Feedback />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/reviews" 
            element={
              <ProtectedRoute>
                <MyReviews />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/baking-tips" 
            element={
              <ProtectedRoute>
                <BakingInstructions />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/order-substitution"
            element={
              <ProtectedRoute>
                <OrderSubstitution />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<RedirectWithParams to="/overview" />} />
        </Routes>
        </RouteErrorBoundary>
        </Suspense>
        
        <AppToaster />

        <FloatingHelpButton />

        {/* Install-the-app promotion (mobile only) — new-look only */}
        {isTest && <PwaInstallBanner />}
        </div>
      </ProductRatingProvider>
    </Router>
  );
}

export default App;
